import { useIntl } from 'react-intl';

import { freightStatusBadgeMessages } from './freight-status-badge-messages';
import { Tag } from '../../tag';
import { activeTrackingStep, FreightTrackingStep, isShipmentCancelled, isShipmentException } from '../freight-tracking-step';

import type { AdlTagColors } from '../../../theme-v2/types';
import type { LtlShipment } from '../../../types/ltl';
import type { MessageDescriptor } from 'react-intl';

type FreightStatusBadgeProps = {
  shipment: LtlShipment;
};

type StatusConfig = { color: AdlTagColors; message: MessageDescriptor };

/**
 * Tag appearance for each position on the tracking ladder, keyed by {@link FreightTrackingStep}
 * so the badge and the Tracking card's stepper can never name the same state differently.
 */
const STEP_CONFIG: Record<FreightTrackingStep, StatusConfig> = {
  [FreightTrackingStep.PendingPickup]: { color: 'info', message: freightStatusBadgeMessages.pending },
  [FreightTrackingStep.InTransit]: { color: 'info', message: freightStatusBadgeMessages.inTransit },
  [FreightTrackingStep.OutForDelivery]: { color: 'info', message: freightStatusBadgeMessages.outForDelivery },
  [FreightTrackingStep.Delivered]: { color: 'success', message: freightStatusBadgeMessages.delivered },
};

const VOIDED_CONFIG: StatusConfig = { color: 'alert', message: freightStatusBadgeMessages.voided };
const EXCEPTION_CONFIG: StatusConfig = { color: 'alert', message: freightStatusBadgeMessages.exception };

/**
 * Voided and Exception are resolved first because neither describes a position — a voided
 * shipment never reaches the ladder at all, and an exception says what went wrong rather than
 * where. Short-circuiting them is also why no events are needed to place the step.
 */
const resolveStatusConfig = (shipment: LtlShipment): StatusConfig => {
  if (isShipmentCancelled(shipment)) return VOIDED_CONFIG;
  if (isShipmentException(shipment)) return EXCEPTION_CONFIG;
  return STEP_CONFIG[activeTrackingStep(shipment, [])];
};

export const FreightStatusBadge = ({ shipment }: FreightStatusBadgeProps) => {
  const intl = useIntl();

  const config = resolveStatusConfig(shipment);

  return (
    <Tag.System color={config.color} data-testid='freight-status-badge' variant='outline'>
      {intl.formatMessage(config.message)}
    </Tag.System>
  );
};
